import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// THE DECISION LEDGER — read-only, append-only-by-nature, and printable.
//
// This is the exhibit. Three years after a de-scope, the question an auditor asks is
// not "what does your software compute" but "show me every release this office granted,
// who signed it, and on what stated basis." That is one page, it prints, and nothing on
// it is editable from here: decisions are written on the De-Scope Review desk and the
// ledger only reads them back.
//
// Deliberately NOT a filterable analytics grid. An exhibit that can be re-sorted into a
// different answer is a worse exhibit.

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api';

type ReviewRow = {
  id: string;
  project_id?: string | null;
  project_code?: string | null;
  subgrantee_label?: string | null;
  requested_n?: number | null;
  counted_n?: number | null;
  reached_n?: number | null;
  // What the determination actually de-obligated. NOT the same number as requested_n —
  // on this box a 200-passing request was decided as a 24-passing partial release.
  released_n?: number | null;
  cost_total?: number | string | null;
  cost_per_passing?: number | string | null;
  ratio?: number | string | null;
  status?: string | null;
  decision?: string | null;
  rationale?: string | null;
  decided_by_name?: string | null;
  received_at?: string | null;
  decided_at?: string | null;
  reversed_at?: string | null;
};

// GET /descope/ledger/verify -> per-row pass/fail over the decision hash chain. Absent
// on a deployment that predates the verifier, in which case the badge simply does not
// claim anything — an unverified chain must never render as a verified one.
type ChainVerify = {
  ok?: boolean;
  checked?: number | null;
  failed?: Array<{ id?: string; reason?: string }> | null;
  rows?: Array<{ id: string; ok: boolean; reason?: string }> | null;
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const usd = (v: unknown, dp = 0) => {
  const n = num(v);
  return n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: dp });
};
const int = (v: unknown) => {
  const n = num(v);
  return n == null ? '—' : n.toLocaleString('en-US');
};
const stamp = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const dateOnly = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const isDecided = (r: ReviewRow) =>
  !!r.decided_at || /release|den|withdraw/i.test(String(r.decision ?? r.status ?? ''));

// Decision vocabulary is the API's, not this page's — we only choose a colour for it.
function decisionTone(d: string): 'danger' | 'warning' | 'success' | 'info' {
  const s = d.toLowerCase();
  if (s.includes('partial')) return 'warning';
  if (s.includes('release')) return 'danger';   // federal money leaves the award
  if (s.includes('den')) return 'success';      // the build obligation is held
  return 'info';
}

const PAGE_SIZE = 50;

export default function GrantorLedgerPage() {
  const [page, setPage] = useState(0);

  const reviews = useQuery<ReviewRow[]>({
    queryKey: ['descope-reviews', 'all'],
    queryFn: async () => {
      const d = await api.get('/descope/reviews');
      return Array.isArray(d) ? d : (d?.reviews ?? d?.rows ?? []);
    },
    retry: false,
  });
  const missing = reviews.isError && /^(404|403)/.test(String((reviews.error as any)?.message ?? ''));

  // Item 14: the chain is only evidence if something checks it. Optional endpoint —
  // when it is not there the badge says the chain was not verified, never that it was.
  const verify = useQuery<ChainVerify>({
    queryKey: ['descope-ledger-verify'],
    queryFn: () => api.get('/descope/ledger/verify') as Promise<ChainVerify>,
    retry: false,
    staleTime: 60 * 1000,
  });
  const verifyFailures = useMemo(() => {
    const v = verify.data;
    if (!v) return [] as Array<{ id?: string; reason?: string }>;
    if (Array.isArray(v.failed)) return v.failed;
    if (Array.isArray(v.rows)) return v.rows.filter((x) => !x.ok);
    return [];
  }, [verify.data]);
  const chainOk = !!verify.data && verifyFailures.length === 0
    && (verify.data.ok === undefined || verify.data.ok === true);

  const decided = useMemo(
    () => (reviews.data ?? [])
      .filter(isDecided)
      .sort((a, b) => new Date(String(b.decided_at ?? 0)).getTime() - new Date(String(a.decided_at ?? 0)).getTime()),
    [reviews.data],
  );

  // Item 23: the register already returns the rationale, the signer and the released
  // count on every row (`/descope/reviews`). The previous version threw that away and
  // re-fetched the full determination document once per decision — N+1 round trips, each
  // carrying the whole resolved id list, to render one column. Everything the exhibit
  // prints now comes from the single list call.
  const pages = Math.max(1, Math.ceil(decided.length / PAGE_SIZE));
  const pageIdx = Math.min(page, pages - 1);

  const muted: CSSProperties = { color: 'var(--text-muted)' };
  const cellNum: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  const releases = decided.filter((r) => /release/i.test(String(r.decision ?? r.status ?? '')));
  // ITEM 7. This tile is the headline of the auditor's exhibit and it summed
  // `requested_n` — what the subgrantee ASKED to be released — while the determinations
  // on the same page released a fraction of that. On this box: 200 requested, 24
  // released, an 8.3x overstatement of federal de-obligation in the document whose whole
  // purpose is to state it correctly. released_n is the count of ids the decision
  // actually un-awarded.
  const totalReleased = releases.reduce((a, r) => a + (num(r.released_n) ?? 0), 0);
  const totalRequested = releases.reduce((a, r) => a + (num(r.requested_n) ?? 0), 0);
  // A determination whose release count never made it into the register cannot be added
  // up honestly; say so rather than quietly counting it as zero.
  const unknownReleaseCounts = releases.filter((r) => num(r.released_n) == null).length;

  return (
    <div className="ledger" style={{ maxWidth: 1180 }}>
      {/* Print rules live with the page that needs them. The global print stylesheet
          already drops the sidebar and topbar; this only handles ledger-specific
          furniture (navigation chrome, row splitting, ink-heavy backgrounds). */}
      <style>{`
        .ledger-offpage { display: none; }
        @media print {
          .ledger-offpage { display: table-row !important; }
          .ledger-noprint { display: none !important; }
          .ledger .card { border: none; box-shadow: none; }
          .ledger .table { font-size: 10.5px; }
          .ledger .table tbody tr { page-break-inside: avoid; }
          .ledger .table tbody tr:hover { background: none; }
          .ledger .pill { border: 1px solid #999; background: none !important; color: #000 !important; }
          .ledger-exhibit-head { display: block !important; }
        }
      `}</style>

      <div className="page__header">
        <div>
          <h1 className="page__title">Decision Ledger</h1>
          <div className="page__subtitle">
            Every de-scope determination this office has issued, newest first. Read-only:
            determinations are recorded on the De-Scope Review desk and cannot be edited or
            removed here. Print this page to file it as an exhibit.
          </div>
        </div>
        <div className="ledger-noprint" style={{ display: 'flex', gap: 8 }}>
          <Link className="btn" to="/grantor">← Oversight Queue</Link>
          <button className="btn btn--primary" onClick={() => window.print()}>Print exhibit</button>
        </div>
      </div>

      {/* Only ink on paper: a printed exhibit has to identify itself away from the app. */}
      <div className="ledger-exhibit-head" style={{ display: 'none', marginBottom: 12 }}>
        <div style={{ fontWeight: 700 }}>De-Scope Determination Ledger</div>
        <div style={{ fontSize: 11 }}>
          Printed {stamp(new Date().toISOString())} · {decided.length} determination(s) of record
        </div>
      </div>

      {missing && (
        <div className="map-banner map-banner--info" style={{ marginBottom: 16 }}>
          The determination register is not answering on this deployment, so no decisions can be
          listed. Nothing has been hidden — this page reads the register and nothing else.
        </div>
      )}

      {!missing && (
        <>
          <div className="stat-grid ledger-noprint" style={{ marginBottom: 18 }}>
            <div className="stat-card stat-card--primary">
              <div className="stat-card__label">Determinations</div>
              <div className="stat-card__value">{reviews.isLoading ? '…' : int(decided.length)}</div>
              <div className="stat-card__delta">recorded on this deployment</div>
            </div>
            {/* Item 7: two tiles, visibly different quantities. What was asked for is not
                what was granted, and only one of them is a federal de-obligation. */}
            <div className="stat-card">
              <div className="stat-card__label">Passings requested</div>
              <div className="stat-card__value">{reviews.isLoading ? '…' : int(totalRequested)}</div>
              <div className="stat-card__delta">what subgrantees asked to be released from</div>
            </div>
            <div className="stat-card stat-card--danger">
              <div className="stat-card__label">Passings released</div>
              <div className="stat-card__value">{reviews.isLoading ? '…' : int(totalReleased)}</div>
              <div className="stat-card__delta">
                actually de-obligated by these determinations
                {unknownReleaseCounts > 0
                  ? ` — ${unknownReleaseCounts} determination(s) did not record a release list and are not counted`
                  : ''}
              </div>
            </div>
          </div>

          {/* Item 14: the hash chain, checked. */}
          <div style={{ marginBottom: 18 }}>
            {verify.isLoading ? (
              <span className="pill pill--info">verifying determination chain…</span>
            ) : verify.isError ? (
              <span className="pill pill--warning" title={String((verify.error as any)?.message ?? '')}>
                chain not verified — this deployment does not answer the verifier
              </span>
            ) : chainOk ? (
              <span className="pill pill--success">
                chain verified — {int(verify.data?.checked ?? decided.length)} determination(s), each
                hash links to the one before it
              </span>
            ) : (
              <span className="pill pill--danger">
                CHAIN BROKEN — {verifyFailures.length} determination(s) do not match their recorded
                hash{verifyFailures[0]?.id ? `, first at ${String(verifyFailures[0].id).slice(0, 8)}` : ''}
                {verifyFailures[0]?.reason ? ` (${verifyFailures[0].reason})` : ''}
              </span>
            )}
          </div>
        </>
      )}

      <div className="card">
        <div className="card__header">
          <span>Determinations of record</span>
          <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>newest first</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Decided</th>
              <th>Reviewer</th>
              <th>Subgrantee</th>
              <th>Project</th>
              <th style={cellNum}>Requested</th>
              <th style={cellNum}>Released</th>
              <th style={cellNum}>Independent cost</th>
              <th style={cellNum}>Ratio to award</th>
              <th>Determination</th>
              <th>Stated basis</th>
              <th className="ledger-noprint" />
            </tr>
          </thead>
          <tbody>
            {reviews.isLoading && <tr><td colSpan={11} style={muted}>Loading ledger…</td></tr>}
            {!reviews.isLoading && !decided.length && (
              <tr>
                <td colSpan={11} style={muted}>
                  No determinations recorded yet. One appears here as soon as a reviewer decides a
                  request on the <Link to="/descope">De-Scope Review</Link> desk.
                </td>
              </tr>
            )}
            {decided.map((r, i) => {
              // Paged on screen, whole on paper: rows outside the current page stay in
              // the document and the print stylesheet brings them back. A printed exhibit
              // that silently stops at row 50 is worse than no pagination at all.
              const onPage = i >= pageIdx * PAGE_SIZE && i < (pageIdx + 1) * PAGE_SIZE;
              const label = String(r.decision ?? r.status ?? 'decided');
              const rationale = r.rationale ? String(r.rationale) : null;
              const who = r.decided_by_name ?? null;
              const ratio = num(r.ratio);
              const isRelease = /release/i.test(label);
              const releasedN = num(r.released_n);
              return (
                <tr key={r.id} className={onPage ? undefined : 'ledger-offpage'}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateOnly(r.decided_at)}</td>
                  <td style={who ? undefined : muted}>{who ? String(who) : 'not recorded'}</td>
                  <td style={{ fontWeight: 600 }}>{r.subgrantee_label ?? '—'}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {r.project_code ?? r.project_id ?? '—'}
                  </td>
                  <td style={cellNum}>{int(r.requested_n)}</td>
                  <td style={{ ...cellNum, fontWeight: isRelease ? 600 : 400 }}>
                    {!isRelease
                      ? <span style={muted}>0</span>
                      : releasedN == null
                        ? <span style={muted} title="This determination did not record the ids it released.">not recorded</span>
                        : int(releasedN)}
                  </td>
                  <td style={cellNum}>{usd(r.cost_total)}</td>
                  <td style={cellNum}>{ratio == null ? '—' : ratio.toFixed(2) + '×'}</td>
                  <td><span className={'pill pill--' + decisionTone(label)}>{label}</span></td>
                  <td style={{ maxWidth: 320 }}>
                    {rationale
                      ? <span title={rationale}>
                          {rationale.length > 160 ? rationale.slice(0, 160).trimEnd() + '…' : rationale}
                        </span>
                      : <span style={muted}>not recorded</span>}
                  </td>
                  <td className="ledger-noprint" style={{ whiteSpace: 'nowrap' }}>
                    <a
                      className="btn"
                      href={`${API_BASE}/descope/reviews/${r.id}/determination.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ padding: '3px 8px', fontSize: 12 }}
                    >
                      PDF
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Item 23: paged on screen so a state with years of determinations gets a
            readable page. Print still emits every row (see .ledger-offpage above) —
            a paginated exhibit is not an exhibit. */}
        {pages > 1 && (
          <div className="ledger-noprint"
               style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px' }}>
            <button className="btn" disabled={pageIdx === 0} onClick={() => setPage(pageIdx - 1)}>
              ← Newer
            </button>
            <span style={{ ...muted, fontSize: 12 }}>
              {pageIdx * PAGE_SIZE + 1}–{Math.min((pageIdx + 1) * PAGE_SIZE, decided.length)} of{' '}
              {decided.length} · page {pageIdx + 1} of {pages}
            </span>
            <button className="btn" disabled={pageIdx >= pages - 1} onClick={() => setPage(pageIdx + 1)}>
              Older →
            </button>
          </div>
        )}
      </div>

      <p style={{ ...muted, fontSize: 12, marginTop: 14, maxWidth: 820 }}>
        Cost figures are independent engineering estimates produced by this office using the same
        design engine the subgrantee is held to. They are not legal cost determinations. The signed
        determination PDF for each row carries the full basis and limitations for that estimate.
      </p>
    </div>
  );
}
